import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";

const WelcomePage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [locationPermission, setLocationPermission] = useState("pending");

  const requestLocationPermission = () => {
    if (!navigator.geolocation) {
      setLocationPermission("unsupported");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      () => setLocationPermission("granted"),
      (error) => {
        if (error.code === 1) {
          setLocationPermission("denied");
        } else {
          setLocationPermission("unavailable");
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // useEffect(() => {
  //   requestLocationPermission();
  // }, []);

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-[#FFF5EB] via-[#FFFFFF] to-[#ECFFF3] flex flex-col items-center justify-center px-6 py-10">

      {/* Background Glow Effects */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-orange-200/40 blur-3xl rounded-full"></div>
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-green-200/40 blur-3xl rounded-full"></div>
      <div className="absolute top-1/3 left-1/4 w-32 h-32 bg-orange-100/40 blur-2xl rounded-full"></div>

      {/* Main Content */}
      <div className="relative z-10 text-center max-w-md w-full">

        {/* Logo Circle */}
        <div className="relative inline-flex items-center justify-center mb-8">
          <div className="absolute inset-0 bg-gradient-to-r from-[#FF7500] to-[#00A650] blur-2xl opacity-30 rounded-full"></div>

          <div className="relative w-32 h-32 rounded-full bg-gradient-to-br from-[#FF7500] to-[#00A650] flex items-center justify-center shadow-2xl border-4 border-white">
            <span className="text-6xl text-white">🍽️</span>
          </div>
        </div>

        {/* Heading */}
        <h1 className="text-3xl font-extrabold text-gray-800 leading-tight tracking-tight">
          {t.global.maaProgramme}
        </h1>

        {/* Divider */}
        <div className="flex justify-center items-center gap-2 mt-4 mb-5">
          <div className="w-14 h-1 rounded-full bg-[#FF7500]"></div>
          <div className="w-14 h-1 rounded-full bg-[#00A650]"></div>
        </div>

        {/* Subtitle */}
        <p className="text-gray-600 text-base font-medium leading-relaxed px-2">
          {t.global.foodQualityMonitoring}
        </p>

        {/* Welcome Text */}
        <div className="mt-10">
          <h2 className="text-2xl font-bold text-gray-800 mb-3">
            {t.global.welcomeToPlatform}
          </h2>

          <p className="text-gray-500 text-sm leading-relaxed">
            {t.global.welcomeMessage}
          </p>
        </div>

        {/* Start Button */}
        <div className="mt-6 text-left w-full max-w-md mx-auto">
          <p className="text-sm text-gray-500 mb-3">
            {locationPermission === "pending" && "Requesting location permission..."}
            {locationPermission === "granted" && "Location access granted. You can now start the survey."}
            {locationPermission === "denied" && "Location permission denied. Please enable location access in your browser settings."}
            {locationPermission === "unsupported" && "Geolocation is not supported by this device."}
            {locationPermission === "unavailable" && "Unable to access location. Please try again."}
          </p>
          {locationPermission !== "granted" && (
            <button
              onClick={requestLocationPermission}
              className="mb-5 w-full bg-white border border-gray-300 text-gray-700 py-3 rounded-xl font-medium hover:bg-red-50 transition"
            >
              Allow location access
            </button>
          )}
        </div>
        <button
          onClick={() => navigate("/survey")}
          className="mt-2 w-full relative overflow-hidden bg-gradient-to-r from-[#FF7500] to-[#00A650] text-white py-5 rounded-3xl flex items-center justify-center gap-4 shadow-2xl transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
        >
          {/* Shine Effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] hover:translate-x-[100%] transition-transform duration-1000"></div>

          <span className="text-2xl">📋</span>

          <span className="text-lg font-semibold tracking-wide">
            {t.global.startSurvey}
          </span>

          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M14 5l7 7m0 0l-7 7m7-7H3"
            />
          </svg>
        </button>

        {/* Info Card */}
        <div className="mt-8 bg-white/80 backdrop-blur-xl rounded-3xl border border-white shadow-xl p-5 text-left">

          <div className="flex items-start gap-4">

            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#FF7500] to-[#00A650] flex items-center justify-center shadow-md flex-shrink-0">
              <span className="text-white text-lg">✓</span>
            </div>

            <div>
              <p className="text-gray-800 text-sm font-semibold mb-1">
                {t.global.quickAnonymous}
              </p>

              <p className="text-gray-500 text-xs leading-relaxed">
                {t.global.surveyInfo}
              </p>
            </div>

          </div>
        </div>

        {/* Footer */}
        <div className="mt-10">
          <p className="text-xs text-gray-500">
            {t.global.poweredBy}
          </p>

          <p className="text-[11px] text-gray-400 mt-2">
            State Urban Development Agency
          </p>
        </div>
      </div>
    </div>
  );
};

export default WelcomePage;